import {
  QueryObserver,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext, useUserByIdQueryClient } from '@chilipiper/fire-session'
import {
  CallerRef,
  CreateWorkspaceRequest,
  ImageUploadRequest,
  ImageUploadRequest2,
  OrgBrandingPutRequest,
  PersonalDetails,
  RevisedOrgBranding,
  RevisedWorkspaceBranding,
  Style,
  UpdateWorkspaceRequest,
  WorkspaceListItem,
} from '@chilipiper/api-client/src/workspace'
import Workspace from '@chilipiper/api-client/src/workspace'
import { usersQueryKeys, usersTableQueryKey, useUser } from './users'

const tenantQueryKeys = {
  tenant: (tenantId: string) => ['tenant', tenantId],
}
export const useTenant = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { data, isLoading } = useQuery({
    queryKey: tenantQueryKeys.tenant(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantid(tenantId),
  })

  return {
    data,
    isLoading,
  }
}

export const useClaimTenant = (options?: { onSuccess?: () => void }) => {
  const { tenantId, userId, invalidateUserData } = useFireSessionContext()

  const { workspace } = useApiClient()
  const queryClient = useQueryClient()
  const userById = useUserByIdQueryClient()
  const tenantQueryKey = tenantQueryKeys.tenant(tenantId)
  const userQueryKey = usersQueryKeys.user(tenantId, userId)

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: () => workspace.postApiV1TenantTenantidClaim(tenantId),
    onMutate: () => {
      userById.cancelQuery(tenantId, userId)
      userById.setQueryData(tenantId, userId, oldData => {
        if (!oldData) {
          return oldData
        }
        return {
          ...oldData,
          isSuperAdmin: true,
          licenses: {
            distro: true,
            chiliCalOrg: true,
            chat: true,
            concierge: true,
            conciergeLive: true,
            handoff: true,
          },
        }
      })

      queryClient.cancelQueries({ queryKey: tenantQueryKey })
      queryClient.setQueryData<ReturnType<typeof useTenant>['data']>(tenantQueryKey, oldData => {
        if (!oldData) {
          return oldData
        }
        return {
          ...oldData,
          claimStatus: 'Claimed',
        }
      })

      queryClient.cancelQueries({ queryKey: userQueryKey })
      queryClient.setQueryData<ReturnType<typeof useUser>['data']>(userQueryKey, oldData => {
        if (!oldData) {
          return oldData
        }
        return {
          ...oldData,
          isSuperAdmin: true,
          licenses: {
            distro: true,
            chiliCalOrg: true,
            chat: true,
            concierge: true,
            conciergeLive: true,
            handoff: true,
          },
        }
      })
    },
    onSettled: () => {
      invalidateUserData?.()
      queryClient.invalidateQueries({ queryKey: tenantQueryKey })
      userById.invalidate(tenantId, userId)
      queryClient.invalidateQueries({ queryKey: userQueryKey })
    },
    ...options,
  })

  return {
    claimTenant: mutate,
    isClaimingTenant: isLoading,
  }
}

const workspaceQueryKeys = {
  workspaces: (tenantId: string, options?: object) => ['workspace', tenantId, options],
  allWorkspaces: (tenantId: string) => ['workspace', tenantId],
  workspace: (tenantId: string, workspaceId: string) => [
    ...workspaceQueryKeys.workspaces(tenantId),
    workspaceId,
  ],
  recentWorkspaces: (tenantId: string, userId: string) => [
    ...workspaceQueryKeys.workspaces(tenantId),
    'recent',
    userId,
  ],
  personal: (tenantId: string, userId: string) => [
    ...workspaceQueryKeys.workspaces(tenantId),
    'personal',
    userId,
  ],
  tenantPicture: (tenantId: string) => ['tenant-picture', tenantId],
  personalDetails: (tenantId: string, userId: string) => [
    'personal-details',
    'tenant',
    tenantId,
    'user',
    userId,
  ],
  getPersonalPage: (tenantId: string, userId: string) => [
    'get-personal-page',
    'tenant',
    tenantId,
    'user',
    userId,
  ],
  findAll: (tenantId: string, search: string, page: number, options: { workspace: string }) => [
    'find-all',
    tenantId,
    page,
    search,
    options,
  ],
  tenantBranding: (tenantId: string) => ['tenant-branding', tenantId],
  tenantBrandingLogo: (tenantId: string) => ['tenant-branding-logo', tenantId],
  workspaceBranding: (tenantId: string, workspaceId: string) => [
    'workspace-branding',
    tenantId,
    workspaceId,
  ],
  workspaceBrandingLogo: (tenantId: string, workspace: string) => [
    'workspace-branding-logo',
    tenantId,
    workspace,
  ],
}

export const useCreateWorkspace = () => {
  const { tenantId, userId } = useFireSessionContext()

  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const {
    mutate,
    mutateAsync,
    isPending: isLoading,
  } = useMutation({
    mutationFn: (payload: CreateWorkspaceRequest) => {
      return workspace.create(tenantId, payload)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceQueryKeys.recentWorkspaces(tenantId, userId),
        }),
        queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.workspaces(tenantId) }),
      ])
    },
  })

  return {
    createWorkspace: mutate,
    createWorkspaceAsync: mutateAsync,
    isCreatingWorkspace: isLoading,
  }
}

export const useGetWorkspaceById = (workspaceId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { data, error, isLoading, fetchStatus } = useQuery({
    queryKey: workspaceQueryKeys.workspace(tenantId, workspaceId as string),
    queryFn: () => workspace.getWorkspace(tenantId, workspaceId as string),
    enabled: !!workspaceId,
    retry: false,
  })

  return {
    error,
    isFetchingWorkspace: fetchStatus === 'fetching' && isLoading, // isLoading is true when query is not enabled
    workspace: data,
  }
}

export const useRecentWorkspaces = () => {
  const { userId, tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const defaultRecentWorkspaces = useMemo(() => [], [])

  const { data, isLoading } = useQuery({
    queryKey: workspaceQueryKeys.recentWorkspaces(tenantId, userId),
    queryFn: () => workspace.getRecentWorkspaces(tenantId, userId),
  })

  return {
    recentWorkspaces: data?.workspaces ?? defaultRecentWorkspaces,
    isFetchingRecentWorkspaces: isLoading,
  }
}

export const useSelectWorkspace = () => {
  const queryClient = useQueryClient()
  const { workspace } = useApiClient()

  const { userId, tenantId } = useFireSessionContext()

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (workspaceId: string) => {
      return workspace.selectWorkspace(tenantId, userId, { workspaceId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.recentWorkspaces(tenantId, userId),
      })
    },
  })

  return {
    selectWorkspace: mutate,
    isSelectingWorkspace: isLoading,
  }
}

export const useUpdateWorkspace = () => {
  const { tenantId, userId } = useFireSessionContext()

  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: ({ id: workspaceId, ...payload }: UpdateWorkspaceRequest & { id: string }) => {
      return workspace.update(tenantId, workspaceId, payload)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceQueryKeys.recentWorkspaces(tenantId, userId),
        }),
        queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.workspaces(tenantId) }),
      ])
    },
  })

  return {
    updateWorkspace: mutate,
    isUpdatingWorkspace: isLoading,
  }
}

export const getWorkspacesQueryOptions = ({
  options = {},
  workspace,
  tenantId,
}: {
  options?: {
    createdBy?: CallerRef
    id?: string[]
    name?: string
    page?: number
    pageSize?: number
    updatedBy?: CallerRef
  }
  tenantId: string
  workspace: Workspace
}) => ({
  queryKey: workspaceQueryKeys.workspaces(tenantId, options),
  queryFn: async ({ pageParam: page }: { pageParam?: number }) => {
    const data = await workspace.query(tenantId, { ...options, page: page ?? 0 })
    return {
      ...data,
      results: data.results ?? [],
    }
  },
})

export const useWorkspaces = (
  options?: {
    createdBy?: CallerRef
    id?: string[]
    name?: string
    page?: number
    pageSize?: number
    updatedBy?: CallerRef
  },
  queryOptions?: { enabled: boolean }
) => {
  const { tenantId } = useFireSessionContext()

  const { workspace } = useApiClient()
  const queryKey = workspaceQueryKeys.workspaces(tenantId, options)

  const { data, fetchNextPage, isFetchingNextPage, isLoading, isFetched } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam: page }) => {
      const data = await workspace.query(tenantId, { ...options, page: page })
      return {
        ...data,
        results: data.results ?? [],
      }
    },
    initialPageParam: 0,
    ...queryOptions,
    getNextPageParam: data => {
      return data.page * data.pageSize < data.total ? data.page + 1 : undefined
    },
  })

  const nextPageFetchPending = useRef(false)
  const queryClient = useQueryClient()

  const fetchMore = () => {
    if (isFetchingNextPage || isLoading) {
      return
    }
    const isSomePageFetching = queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching'
    if (isSomePageFetching) {
      nextPageFetchPending.current = true
      const observer = new QueryObserver(queryClient, { queryKey })
      const unsubscribe = observer.subscribe(({ isFetching }) => {
        if (!isFetching && nextPageFetchPending.current) {
          nextPageFetchPending.current = false
          fetchNextPage()
          unsubscribe()
        }
      })
    } else {
      fetchNextPage()
    }
  }

  const total = data?.pages?.[0]?.total ?? 0

  const mergedData = useMemo(() => {
    const allData: WorkspaceListItem[] = []
    for (const page of data?.pages ?? []) {
      allData.push(...(page.results ?? []))
    }
    return allData
  }, [data?.pages])

  return {
    workspaces: mergedData,
    fetchMore,
    isLoading,
    total,
    isFetched,
  }
}

export const useCurrentUserPersonalWorkspace = (options: { suspense?: boolean } = {}) => {
  const { userId } = useFireSessionContext()

  return usePersonalWorkspace(userId, options)
}

export const usePersonalWorkspace = (userId: string = '', options: { suspense?: boolean } = {}) => {
  const { tenantId } = useFireSessionContext()

  const { workspace } = useApiClient()
  return useQuery({
    queryKey: workspaceQueryKeys.personal(tenantId, userId),
    queryFn: () => workspace.getPersonal(tenantId, userId),
    ...options,
    enabled: Boolean(userId),
  })
}

const uiDataQueryKey = (tenantId: string, userId: string) => ['uiData', tenantId, userId]
export const useUiData = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: uiDataQueryKey(tenantId, userId),
    queryFn: () => workspace.getUiData(tenantId, userId),
  })
}

export const useSetUiData = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (payload: any) => {
      return workspace.setUiData(tenantId, userId, payload)
    },
    onMutate: payload => {
      queryClient.cancelQueries({ queryKey: uiDataQueryKey(tenantId, userId) })
      queryClient.setQueryData(uiDataQueryKey(tenantId, userId), payload)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: uiDataQueryKey(tenantId, userId) })
    },
  })

  return {
    mutate,
  }
}

const QUERY_PAGE_SIZE = 1_000_000

export const useAllWorkspaces = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { data } = useQuery({
    queryKey: workspaceQueryKeys.allWorkspaces(tenantId),
    queryFn: () =>
      workspace.query(tenantId, {
        page: 0,
        pageSize: QUERY_PAGE_SIZE,
      }),
  })

  return { workspaces: data?.results ?? [] }
}

export const useTenantPicture = (tenantId: string) => {
  const { workspace } = useApiClient()

  const { data } = useQuery({
    queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantidPicture(tenantId),
  })

  return { companyLogo: data?.url }
}

export const useTenantPictureUpdate = (tenantId: string) => {
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (data: ImageUploadRequest) => {
      return workspace.putApiV1TenantTenantidPicture(tenantId, data)
    },
    onMutate: async companyLogo => {
      await queryClient.cancelQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })

      const previousImage = queryClient.getQueryData(
        workspaceQueryKeys.tenantBrandingLogo(tenantId)
      )
      queryClient.setQueryData(workspaceQueryKeys.tenantBrandingLogo(tenantId), {
        url: URL.createObjectURL(companyLogo.image as Blob),
      })

      return { previousImage }
    },
    onError: (_old, _new, context) => {
      queryClient.setQueryData(
        workspaceQueryKeys.tenantBrandingLogo(tenantId),
        context?.previousImage
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })
    },
  })

  return {
    onUpdateCompanyLogo: mutate,
  }
}

export const useTenantPictureDelete = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: () => {
      return workspace.deleteApiV1TenantTenantidPicture(tenantId)
    },
    onMutate: async () => {
      queryClient.setQueryData(workspaceQueryKeys.tenantBrandingLogo(tenantId), {})
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })
    },
  })

  return {
    onDeleteCompanyLogo: mutate,
  }
}

export const useSetPersonalDetails = (userId?: string) => {
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUpdate = userId ?? currentUserId

  const queryKey = workspaceQueryKeys.personalDetails(tenantId, userIdToUpdate)

  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: (personalDetails: PersonalDetails) => {
      return workspace.setPersonalDetails(tenantId, userIdToUpdate, personalDetails)
    },
    onMutate: async newData => {
      await queryClient.cancelQueries({ queryKey })
      const previousPersonalDetails = queryClient.getQueryData<PersonalDetails>(queryKey)
      queryClient.setQueryData(queryKey, newData)
      return { previousPersonalDetails, updatedPersonalDetails: newData }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries({ queryKey: usersTableQueryKey })

      // Invalidating query from apps/admin-center/src/api-queries/personalDetails.ts
      if (userIdToUpdate === currentUserId) {
        void queryClient.invalidateQueries({
          queryKey: ['personalDetails', tenantId, currentUserId],
        })
      }
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(queryKey, context?.previousPersonalDetails)
    },
  })
  return {
    mutateAsync,
    isLoading,
  }
}

export const getPersonalDetailsQueryOptions = ({
  workspace,
  tenantId,
  userId,
}: {
  tenantId: string
  userId: string
  workspace: Workspace
}) => ({
  queryKey: workspaceQueryKeys.personalDetails(tenantId, userId),
  queryFn: () => workspace.findPersonalDetailsForGuests(tenantId, userId),
})

export const useCurrentUserPersonalDetails = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const { queryKey, queryFn } = getPersonalDetailsQueryOptions({ workspace, tenantId, userId })
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn,
    retryOnMount: false,
    retry: (_, error) => 'status' in error && error.status !== 404,
  })
  return {
    data,
    isLoading,
    isError,
  }
}

export const usePersonalDetails = (userId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const { queryKey, queryFn } = getPersonalDetailsQueryOptions({
    workspace,
    tenantId,
    userId: userId ?? '',
  })
  return useQuery({
    queryKey,
    queryFn,
    retryOnMount: false,
    retry: (_, error) => 'status' in error && error.status !== 404,
    enabled: !!userId,
  })
}

export const usePersonalPage = (userId: string) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: workspaceQueryKeys.getPersonalPage(tenantId, userId),
    queryFn: () => workspace.getPersonalPageForGuests(tenantId, userId),
  })
}

export const useTenantBrandingSettings = (options?: { enabled: boolean }) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: workspaceQueryKeys.tenantBranding(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantidBranding(tenantId),
    ...options,
  })
}

export const useUpdateTenantBranding = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()
  const { data: branding } = useTenantBrandingSettings()

  const revision = String(branding?.metadata?.revision ?? 0)

  const headers = {
    headers: { 'If-Match': `"${revision}"` },
  }

  const previousSettings = queryClient.getQueryData<RevisedOrgBranding>(
    workspaceQueryKeys.tenantBranding(tenantId)
  )

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: (settings: OrgBrandingPutRequest) => {
      return workspace.putApiV1TenantTenantidBranding(tenantId, settings, {
        ...headers,
      })
    },
    onMutate: async settings => {
      queryClient.setQueryData(workspaceQueryKeys.tenantBranding(tenantId), {
        ...previousSettings,
        branding: settings,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBranding(tenantId) })
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId) })
    },
    onError: () => {
      queryClient.setQueryData(workspaceQueryKeys.tenantBranding(tenantId), previousSettings)
    },
  })

  return {
    onUpdateTenantBranding: mutateAsync,
    isLoading,
  }
}

export const useTenantLogo = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantidLogo(tenantId),
  })
}

export const useUpdateTenantLogo = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (logo: ImageUploadRequest2) => {
      return workspace.putApiV1TenantTenantidLogo(tenantId, logo)
    },
    onMutate: async image => {
      queryClient.setQueryData(workspaceQueryKeys.tenantBrandingLogo(tenantId), image)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBranding(tenantId) })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId) })
    },
  })

  return {
    onUpdateTenantLogo: mutate,
  }
}

export const useDeleteTenantLogo = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: () => workspace.deleteApiV1TenantTenantidLogo(tenantId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBrandingLogo(tenantId) })
      queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.tenantBranding(tenantId) })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId) })
    },
  })

  return {
    onDeleteTenantLogo: mutateAsync,
  }
}

export const useWorkspaceBranding = (workspaceId?: string, options?: { enabled: boolean }) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: workspaceQueryKeys.workspaceBranding(tenantId, workspaceId ?? ''),
    queryFn: () =>
      workspace.getApiV1TenantTenantidWorkspaceWorkspaceidBranding(tenantId, workspaceId ?? ''),
    enabled: Boolean(workspaceId) && options ? options.enabled : true,
  })
}

export const useDeleteWorkspaceBranding = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: ({ workspaceId }: { workspaceId: string }) => {
      return workspace.deleteApiV1TenantTenantidWorkspaceWorkspaceidBrandingStyle(
        tenantId,
        workspaceId
      )
    },
    onSettled: (_, __, { workspaceId }) => {
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBranding(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBrandingLogo(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId, workspaceId) })
    },
  })

  return {
    onDeleteWorkspaceBranding: mutateAsync,
  }
}

export const useUpdateWorkspaceBranding = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({
      settings,
      revision,
      workspaceId,
    }: {
      revision: number
      settings: Style
      workspaceId: string
    }) => {
      return workspace.putApiV1TenantTenantidWorkspaceWorkspaceidBrandingStyle(
        tenantId,
        workspaceId,
        settings,
        {
          headers: { 'If-Match': `"${revision}"` },
        }
      )
    },
    onMutate: async ({ settings, workspaceId }) => {
      const previousSettings = queryClient.getQueryData<RevisedWorkspaceBranding>(
        workspaceQueryKeys.workspaceBranding(tenantId, workspaceId)
      )

      queryClient.setQueryData(workspaceQueryKeys.workspaceBranding(tenantId, workspaceId), {
        ...previousSettings,
        branding: settings,
      })
    },
    onSettled: (_, __, { workspaceId }) => {
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBranding(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBrandingLogo(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId, workspaceId) })
    },
    onError: (_, { workspaceId }) => {
      const previousSettings = queryClient.getQueryData<RevisedWorkspaceBranding>(
        workspaceQueryKeys.workspaceBranding(tenantId, workspaceId)
      )
      queryClient.setQueryData(
        workspaceQueryKeys.workspaceBranding(tenantId, workspaceId),
        previousSettings
      )
    },
  })

  return {
    onUpdateWorkspaceBranding: mutateAsync,
    isLoading,
  }
}

export const useWorkspaceLogo = (workspaceId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: workspaceQueryKeys.workspaceBrandingLogo(tenantId, workspaceId as string),
    queryFn: () =>
      workspace.getApiV1TenantTenantidWorkspaceWorkspaceidLogo(tenantId, workspaceId as string),
    enabled: Boolean(workspaceId),
  })
}

export const useUpdateWorkspaceLogo = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: ({ workspaceId, logo }: { logo: ImageUploadRequest2; workspaceId: string }) => {
      return workspace.putApiV1TenantTenantidWorkspaceWorkspaceidLogo(tenantId, workspaceId, logo)
    },
    onSettled: (_, __, { workspaceId }) => {
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBranding(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBrandingLogo(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId, workspaceId) })
    },
  })

  return {
    onUpdateWorkspaceLogo: mutateAsync,
  }
}

export const useDeleteWorkspaceLogo = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: ({ workspaceId }: { workspaceId: string }) => {
      return workspace.deleteApiV1TenantTenantidWorkspaceWorkspaceidLogo(tenantId, workspaceId)
    },
    onSettled: (_, __, { workspaceId }) => {
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBranding(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.workspaceBrandingLogo(tenantId, workspaceId),
      })
      queryClient.invalidateQueries({ queryKey: publicStylingKey(tenantId, workspaceId) })
    },
  })

  return {
    onDeleteWorkspaceLogo: mutateAsync,
  }
}

const publicStylingKey = (tenantId: string, workspaceId?: string) =>
  ['public-styling', tenantId, workspaceId].filter(key => key !== undefined)
export const usePublicStyling = (tenantId: string | undefined, workspaceId: string | undefined) => {
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: publicStylingKey(tenantId as string, workspaceId as string),
    queryFn: () =>
      workspace.publicGetWorkspaceAppliedStylingGuest(tenantId as string, workspaceId as string),
    enabled: Boolean(tenantId && workspaceId),
  })
}
